import { getIntersectionRoot } from '@theme/scroll-container';

/**
 * Product card video preview.
 *
 * Desktop (fine pointer): the clip loads and plays on hover of the card's media
 * area, and rewinds when the pointer leaves.
 *
 * Touch: there is no hover, so a single shared IntersectionObserver picks the
 * card nearest the vertical centre of the viewport and plays only that one.
 *
 * Two invariants hold in both modes:
 *
 * 1. Nothing is downloaded until playback is actually wanted. The markup ships
 *    without a `src`; it is assigned on first intent. `preload="none"` alone
 *    would not be enough — a `src` still costs a metadata request per card.
 *
 * 2. At most one video plays at a time, tracked in a module-level reference.
 *    Without it, flicking the cursor across a grid leaves a trail of clips
 *    running behind their own cards.
 */

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const canHover = matchMedia('(hover: hover) and (pointer: fine)');

/** The one element allowed to be playing. @type {FbCardVideo | null} */
let activeVideo = null;

/** Registry of touch-mode elements sharing a single observer. @type {Set<FbCardVideo>} */
const scrollCandidates = new Set();

/** @type {IntersectionObserver | undefined} */
let scrollObserver;

/**
 * Choose the registered card closest to the vertical centre of the viewport and
 * hand playback to it. Called from the observer rather than a scroll listener,
 * so it only runs when visibility actually changes.
 */
function selectMostCentredCard() {
  const viewportCentre = window.innerHeight / 2;
  let best = null;
  let bestDistance = Infinity;

  for (const element of scrollCandidates) {
    if (!element.isConnected) {
      scrollCandidates.delete(element);
      continue;
    }
    if (!element.visibleEnough) continue;

    const rect = element.getBoundingClientRect();
    const distance = Math.abs(rect.top + rect.height / 2 - viewportCentre);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = element;
    }
  }

  if (best !== activeVideo) activeVideo?.stop();
  best?.play();
}

function getScrollObserver() {
  scrollObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const element = /** @type {FbCardVideo} */ (entry.target);
        // 0.6 keeps a card from taking over while it is only edging into view.
        element.visibleEnough = entry.intersectionRatio >= 0.6;
        // Prepare slightly ahead of playing so the fade-in is not a cold start.
        if (entry.isIntersecting) element.prepare();
      }
      selectMostCentredCard();
    },
    { root: getIntersectionRoot(), threshold: [0, 0.6, 0.9] }
  );
  return scrollObserver;
}

class FbCardVideo extends HTMLElement {
  /** Set by the observer; read back by the centre-selection pass. */
  visibleEnough = false;

  #video = null;
  #sourceAttached = false;
  #controller = null;

  connectedCallback() {
    // Reduced motion keeps the still image and never fetches the clip at all.
    if (reducedMotion.matches) return;

    this.#video = this.querySelector('video');
    if (!(this.#video instanceof HTMLVideoElement)) return;

    // Belt and braces: muted is required for autoplay and must be set as a
    // property, not just the attribute, for Safari to honour it.
    this.#video.muted = true;
    this.#video.defaultMuted = true;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#video.addEventListener('playing', () => this.#markPlaying(), { signal });
    // A clip that fails to load must not leave a half-faded overlay behind.
    this.#video.addEventListener('error', () => this.stop(), { signal });

    if (canHover.matches) {
      // The gallery is the hover target, not this element — this element has
      // pointer-events disabled so the product link stays clickable.
      const target = this.closest('.card-gallery') ?? this;
      target.addEventListener('pointerenter', this.#onEnter, { signal });
      target.addEventListener('pointerleave', this.#onLeave, { signal });
      // Cheap head start: fetch metadata as the cursor nears the card.
      target.addEventListener('pointermove', this.prepare, { once: true, passive: true, signal });
    } else {
      scrollCandidates.add(this);
      getScrollObserver().observe(this);
    }
  }

  disconnectedCallback() {
    this.stop();
    this.#controller?.abort();
    scrollCandidates.delete(this);
    scrollObserver?.unobserve(this);
  }

  /** @param {PointerEvent} event */
  #onEnter = (event) => {
    if (event.pointerType === 'touch') return;
    this.play();
  };

  /** @param {PointerEvent} event */
  #onLeave = (event) => {
    if (event.pointerType === 'touch') return;
    this.stop();
  };

  /** Attach the source. Idempotent, and the only place a network request starts. */
  prepare = () => {
    if (this.#sourceAttached || !this.#video) return;
    const src = this.dataset.videoSrc;
    if (!src) return;
    this.#sourceAttached = true;
    this.#video.src = src;
    this.#video.load();
  };

  play() {
    if (!this.#video || reducedMotion.matches) return;
    this.prepare();

    if (activeVideo && activeVideo !== this) activeVideo.stop();
    activeVideo = this;

    const played = this.#video.play();
    // Autoplay rejection (or an aborted play from a fast pointer-out) must not
    // surface as an unhandled rejection.
    played?.catch?.(() => {});
  }

  stop() {
    if (activeVideo === this) activeVideo = null;
    if (!this.#video) return;
    this.#video.pause();
    // Rewind so the next hover always opens on the first frame.
    if (this.#video.currentTime) this.#video.currentTime = 0;
    delete this.dataset.fbPlaying;
  }

  /**
   * Fade in only once frames are actually rendering. Guarded against a late
   * `playing` event arriving after the pointer already left.
   */
  #markPlaying() {
    if (activeVideo !== this) {
      this.#video?.pause();
      return;
    }
    this.dataset.fbPlaying = '';
  }
}

if (!customElements.get('fb-card-video')) {
  customElements.define('fb-card-video', FbCardVideo);
}
